/**
 * Evaluación de los filtros de publicación (grupos "Publicación" y
 * "Características de la unidad") sobre lotes agrupados: lista compartida,
 * registros propios o una lista del usuario.
 *
 * El match por definición lo hace `matchesFilter` de `parcelFilterRowMatch`; acá
 * está lo que ese motor no sabe: que los datos son del dueño (modalidad, precio,
 * ambientes, servicios) y viven en cada UF o en el contenido del lote, no en el
 * catastro. El lote entra si alguna UF o `lot_user_content` cumple (OR). El
 * listado recorta `units[]` a las que matchean; si solo cumplió el terreno, la
 * card queda como contenido de lote. Sin UF ni contenido, se evalúa la raíz.
 *
 * @see docs/maps/shared-list-view.md
 * @see docs/maps/parcel-filters-scope-backend-spec.md §5
 */

import {
  countActiveParcelFilters,
  getListingFilterDefinitions,
  isFilterValueActive,
} from '../data/parcelFilterContract';
import { matchesFilter, filterRowsByParcelFilters } from '../data/parcelFilterRowMatch';
import { getDisplayUnits } from './userLotUnits';
import { mapOperationFromApi } from './userLotFormUtils';
import { normalizeBuildingType } from './ufAttributeFormUtils';
import { filterGroupedLotForSharedSearch } from './brandedListingItems';
import { CURRENCIES } from './listingCoreConstants';
import { normalizeNlQuery } from './nlSearchQuery';

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCurrency = (value) => {
  const text = String(value ?? '').trim().toUpperCase();
  return text === '' ? null : text;
};

/** Superficie total declarada, o la suma de cubierta y descubierta como la card. */
const resolveTotalSurface = (source) => {
  const total = toNumber(source?.total_surface);
  if (total != null) return total;

  const covered = toNumber(source?.covered_surface);
  const open = toNumber(source?.open_surface);
  if (covered == null && open == null) return null;
  return (covered || 0) + (open || 0);
};

const firstPresent = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
};

const LOT_PUBLICATION_KEYS = [
  'operation', 'price_type', 'price', 'currency',
  'title', 'description', 'observations', 'notes', 'unit_label',
];

/**
 * Publicación del terreno: `lot_user_content` + raíz de la card +
 * `private_attributes`. Mismos campos que una UF (modalidad, moneda, precio,
 * texto). Un bloque incompleto no tapa al otro.
 */
const resolveLotPublicationSource = (lot) => {
  if (!lot) return null;
  const content = lot.lot_user_content && typeof lot.lot_user_content === 'object'
    ? lot.lot_user_content
    : null;
  const rootAttrs = lot.private_attributes && typeof lot.private_attributes === 'object'
    ? lot.private_attributes
    : null;
  const contentAttrs = content?.private_attributes && typeof content.private_attributes === 'object'
    ? content.private_attributes
    : null;

  const source = {
    private_attributes: { ...rootAttrs, ...contentAttrs },
  };
  LOT_PUBLICATION_KEYS.forEach((key) => {
    source[key] = firstPresent(
      content?.[key],
      contentAttrs?.[key],
      lot[key],
      rootAttrs?.[key],
    );
  });
  const hasAny = LOT_PUBLICATION_KEYS.some((key) => source[key] != null);
  return hasAny ? source : null;
};

/**
 * Publicación lista para el evaluador genérico: `private_attributes` aplanado en
 * la raíz (si quedara como bloque tendría prioridad y pisaría lo normalizado) y
 * los campos ambiguos resueltos, porque el contrato no conoce los legacy
 * (`price_type`), los alias de tipo de unidad ni la variación de mayúsculas.
 *
 * Modalidad y moneda se leen del objeto ya aplanado. Si `operation` se tomara
 * del source crudo, un `private_attributes.operation` (típico del lote) se
 * pisaría con null y el filtro de moneda seguiría andando.
 */
const normalizeListingRow = (source) => {
  const { private_attributes: privateAttrs, ...rest } = source || {};
  const flat = { ...rest };
  if (privateAttrs && typeof privateAttrs === 'object') {
    Object.entries(privateAttrs).forEach(([key, value]) => {
      if (value == null) return;
      if (typeof value === 'string' && value.trim() === '') return;
      flat[key] = value;
    });
  }

  return {
    ...flat,
    operation: mapOperationFromApi(flat),
    currency: normalizeCurrency(flat.currency),
    building_type: normalizeBuildingType(flat.building_type) || null,
    total_surface: resolveTotalSurface(flat),
  };
};

const activeValues = (filters, field) => {
  const def = getListingFilterDefinitions().find((d) => d.field === field);
  const value = filters?.[field];
  return def && isFilterValueActive(value, def) ? value : null;
};

/** Definiciones con valor activo, resueltas una vez por corrida y no por unidad. */
const readActiveListingDefs = (filters) => getListingFilterDefinitions()
  .filter((def) => def.field !== 'listing_search' && isFilterValueActive(filters[def.field], def))
  .map((def) => ({ def, value: filters[def.field] }));

const matchesActiveDefs = (source, activeDefs) => {
  const row = normalizeListingRow(source);
  return activeDefs.every(({ def, value }) => matchesFilter(def, row, value));
};

const lotPublicationMatchesSearch = (lot, term) => {
  const source = resolveLotPublicationSource(lot);
  if (!source) return false;
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return true;
  const operation = mapOperationFromApi(source);
  const haystack = [
    source.title,
    source.description,
    source.observations,
    source.notes,
    source.unit_label,
    operation,
    source.currency,
    source.price != null && source.price !== '' ? String(source.price) : null,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(needle);
};

/** Card que sobrevivió solo por el contenido del terreno, no por las UF. */
const keepLotForContentMatch = (lot) => ({
  ...lot,
  units: [],
  unit_count: 0,
  has_lot_content: true,
  item_type: lot.item_type === 'marker' ? 'marker' : (lot.item_type || 'lot_content'),
});

/**
 * Lote con sus unidades reducidas a las que cumplen. UF y contenido del lote
 * son OR: un terreno en venta no queda afuera porque las UF estén en alquiler.
 * Sin UF ni contenido, la publicación vive en la raíz (campos de unidad en null).
 */
const filterLotByActiveDefs = (lot, activeDefs) => {
  if (!lot) return null;

  const lotPublication = resolveLotPublicationSource(lot);
  const contentMatches = Boolean(lotPublication) && matchesActiveDefs(lotPublication, activeDefs);
  const units = getDisplayUnits(lot);

  if (units.length) {
    const matching = units.filter((unit) => matchesActiveDefs(unit, activeDefs));
    if (matching.length) {
      if (matching.length === units.length) return lot;
      return { ...lot, units: matching, unit_count: matching.length };
    }
    return contentMatches ? keepLotForContentMatch(lot) : null;
  }

  return contentMatches ? lot : null;
};

/** Cantidad de filtros de publicación activos. */
export function countActiveListingFilters(filters) {
  if (!filters) return 0;
  return getListingFilterDefinitions().reduce(
    (count, def) => (isFilterValueActive(filters[def.field], def) ? count + 1 : count),
    0,
  );
}

/**
 * Lotes agrupados que cumplen los filtros de publicación, con `units[]` reducidas.
 * @param {Array<object>} lots lotes agrupados (`groupUnitsByLot`)
 * @param {Record<string, unknown>} filters estado del panel
 * @returns {Array<object>}
 */
export function filterGroupedLotsByListingFilters(lots, filters) {
  if (!Array.isArray(lots) || lots.length === 0) return [];
  if (countActiveListingFilters(filters) === 0) return lots;

  const search = activeValues(filters, 'listing_search');
  const afterSearch = search
    ? lots.map((lot) => {
      const byItems = filterGroupedLotForSharedSearch(lot, search);
      if (byItems) return byItems;
      if (!lotPublicationMatchesSearch(lot, search)) return null;
      return getDisplayUnits(lot).length ? keepLotForContentMatch(lot) : lot;
    }).filter(Boolean)
    : lots;

  const activeDefs = readActiveListingDefs(filters);
  if (activeDefs.length === 0) return afterSearch;

  return afterSearch
    .map((lot) => filterLotByActiveDefs(lot, activeDefs))
    .filter(Boolean);
}

/** Precios cargados en la lista para una moneda. */
const collectListingPrices = (lots, currency) => {
  const target = normalizeCurrency(currency);
  if (!target) return [];

  const prices = [];
  const collect = (source) => {
    const price = toNumber(source?.price);
    if (price != null && price > 0 && normalizeCurrency(source?.currency) === target) {
      prices.push(price);
    }
  };

  (lots || []).forEach((lot) => {
    const units = getDisplayUnits(lot);
    if (units.length) units.forEach(collect);
    const publication = resolveLotPublicationSource(lot);
    if (publication) collect(publication);
  });

  return prices.sort((a, b) => a - b);
};

/** Redondea a un corte legible (1, 2, 2.5 o 5 por magnitud). */
const roundNice = (value) => {
  if (value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const candidates = [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude);
  return candidates.reduce(
    (best, candidate) =>
      (Math.abs(candidate - value) < Math.abs(best - value) ? candidate : best),
    candidates[0],
  );
};

const formatAmount = (value) => {
  const compact = (amount, suffix) =>
    `${amount.toLocaleString('es-AR', { maximumFractionDigits: 1 })} ${suffix}`;
  if (value >= 1e6) return compact(value / 1e6, 'M');
  if (value >= 1e3) return compact(value / 1e3, 'k');
  return value.toLocaleString('es-AR', { maximumFractionDigits: 0 });
};

/**
 * Bandas de precio derivadas de las publicaciones de la lista en una moneda.
 * Los cortes salen de los cuartiles redondeados: una lista de alquileres y otra
 * de ventas no comparten escala, así que un catálogo fijo dejaría todo en una
 * sola banda.
 *
 * @param {Array<object>} lots lotes agrupados
 * @param {string} currency moneda seleccionada en el panel
 * @returns {Array<{ key: string, value: string, label: string, shortLabel: string }>}
 *   vacío si no hay precios suficientes para armar tramos
 */
export function buildListingPriceBandOptions(lots, currency) {
  const prices = collectListingPrices(lots, currency);
  if (prices.length < 3) return [];

  const quantile = (ratio) => prices[Math.min(prices.length - 1, Math.floor(prices.length * ratio))];
  const cuts = Array.from(new Set([0.25, 0.5, 0.75].map((ratio) => roundNice(quantile(ratio)))))
    .filter((cut) => cut > prices[0] && cut <= prices[prices.length - 1])
    .sort((a, b) => a - b);

  if (cuts.length === 0) return [];

  const bounds = [null, ...cuts, null];
  const options = [];

  for (let i = 0; i < bounds.length - 1; i += 1) {
    const min = bounds[i];
    const max = bounds[i + 1];
    const label = min == null
      ? `< ${formatAmount(max)}`
      : (max == null ? `≥ ${formatAmount(min)}` : `${formatAmount(min)} – ${formatAmount(max)}`);

    // Contrato parse: "min:max" (max vacío = sin techo). El piso abierto va
    // como 0, igual que el ejemplo `["0:80000", …, "250000:"]`.
    const bandValue = `${min ?? 0}:${max ?? ''}`;
    options.push({
      key: bandValue,
      value: bandValue,
      label,
      shortLabel: label,
    });
  }

  return options;
}

const PHRASE_CURRENCY_RE = {
  EUR: /\b(euros?|eur)\b/,
  USD: /\b(dolares?|usd|dollars?)\b/,
  ARS: /\b(pesos?|ars)\b/,
};
const ALQUILER_RE = /\balquiler(\s+temporario)?\b/;
const VENTA_RE = /\bventa\b/;

/**
 * Moneda nombrada en la frase (no la del parse anterior).
 * @param {string} query
 * @returns {'USD'|'ARS'|'EUR'|null}
 */
export function inferListingParsePhraseCurrency(query) {
  const nq = normalizeNlQuery(query);
  if (PHRASE_CURRENCY_RE.EUR.test(nq)) return 'EUR';
  if (PHRASE_CURRENCY_RE.USD.test(nq)) return 'USD';
  if (PHRASE_CURRENCY_RE.ARS.test(nq)) return 'ARS';
  return null;
}

/**
 * De qué moneda sacar `listing_price_bands` si el usuario no tocó el chip.
 * Default USD (venta). ARS si la frase dice pesos o es alquiler sin venta.
 * @param {string} query
 * @returns {'USD'|'ARS'|'EUR'}
 */
export function inferListingParseBandCurrency(query) {
  const explicit = inferListingParsePhraseCurrency(query);
  if (explicit) return explicit;
  const nq = normalizeNlQuery(query);
  if (ALQUILER_RE.test(nq) && !VENTA_RE.test(nq)) return 'ARS';
  return 'USD';
}

const bandValuesFor = (lots, currency) => (
  buildListingPriceBandOptions(lots, currency).map((opt) => opt.value).filter(Boolean)
);

/**
 * Extra del POST `/parsesearchquery` en lista compartida.
 * Cada Enter es como el primero de la sesión: no reenvía la moneda que dejó
 * el parse anterior. `listing_currency` solo si la frase nombra moneda o el
 * usuario tocó el chip. Bandas: USD si hay cortes en USD (venta); ARS si
 * pesos / alquiler / chip ARS.
 *
 * @param {Array<object>} lots
 * @param {object} [options]
 * @param {string} [options.query]
 * @param {string[]} [options.selectedCurrencies]
 * @param {boolean} [options.currencyChosenByUser]
 * @returns {{ listingCurrency: string|null, listingPriceBands: string[] }}
 */
export function resolveListingParsePricePayload(lots, {
  query = '',
  selectedCurrencies = [],
  currencyChosenByUser = false,
} = {}) {
  const selected = (selectedCurrencies || [])
    .map((value) => String(value || '').trim().toUpperCase())
    .filter(Boolean);

  if (currencyChosenByUser && selected.length === 1) {
    return {
      listingCurrency: selected[0],
      listingPriceBands: bandValuesFor(lots, selected[0]),
    };
  }

  const phraseCurrency = inferListingParsePhraseCurrency(query);
  const preferred = inferListingParseBandCurrency(query);
  const order = [preferred, ...CURRENCIES.filter((item) => item !== preferred)];

  for (let i = 0; i < order.length; i += 1) {
    const listingPriceBands = bandValuesFor(lots, order[i]);
    if (!listingPriceBands.length) continue;
    return {
      listingCurrency: phraseCurrency && order[i] === phraseCurrency ? phraseCurrency : null,
      listingPriceBands,
    };
  }

  return { listingCurrency: phraseCurrency, listingPriceBands: [] };
}

/**
 * AND de publicación (UF o contenido del lote) y atributos de parcela, mismo orden que la vista
 * compartida. Para Registros, detalle de lista y pines del alcance en el mapa.
 * @param {Array<object>} lots
 * @param {Record<string, unknown>} filters
 * @returns {Array<object>}
 */
export function filterOwnedLotsByPanelFilters(lots, filters) {
  if (!Array.isArray(lots) || lots.length === 0) return [];
  const listingCount = countActiveListingFilters(filters);
  const parcelCount = countActiveParcelFilters(filters) - listingCount;
  let rows = lots;
  if (listingCount > 0) rows = filterGroupedLotsByListingFilters(rows, filters);
  if (parcelCount > 0) rows = filterRowsByParcelFilters(rows, filters);
  return rows;
}
